// packages/ui/src/hooks/useDisplayFx.ts
//
// Phase 8.2 Wave 1 Task 05 — Shared live FX hook for `<Money>`.
//
// vg-binding: CONTEXT:decisions=P8.D-186
// vg-binding: BR:business-rules=BR-8
// vg-binding: CONTEXT:UI-SPEC=§0.1
// vg-binding: API-CONTRACTS:fx-rates-endpoint=P6.D-230
//
// `useDisplayFx(displayCcy)` — TanStack Query wrapper around the live FX
// endpoint `GET /api/v1/fx-rates?from=USD&to={ccy}`. USD is the
// canonical currency; every Money cell renders the
// USD source amount and uses this hook to resolve a live conversion for
// the `(EUR x,xxx.xx)` companion + tooltip.
//
// Caching policy (per task plan):
//   • staleTime = 5 min            — FX rates change slowly; avoids burst
//                                     refetches while a long-running admin
//                                     shift hops surfaces.
//   • gcTime    = 30 min           — keep the cached rate for re-entry to
//                                     the same surface without a refetch.
//   • refetchOnWindowFocus = true  — pull a fresh rate when the admin tabs
//                                     back in after a break.
//   • retry = 1                    — single retry on transient network glitch.
//
// USD ⇒ USD short-circuit: when `displayCcy === 'USD'` (or no displayCcy)
// the hook returns `{ fxRate: 1, fetchedAt: now, ccy: 'USD' }` synchronously
// without hitting the network — Money components on USD-only surfaces don't
// pay the round-trip cost.
//
// Envelope tolerance: accepts both the canonical INTERFACE-STANDARDS
// envelope `{ ok, data: { rate, fetchedAt } }` and the legacy flat shape
// `{ rate, fetchedAt }` per the standards' "legacy compact shape" clause.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

/** Stale threshold — 5 minutes matches React Query staleTime. */
export const DISPLAY_FX_STALE_THRESHOLD_MS = 5 * 60 * 1000;

/** TanStack Query cache GC time — 30 minutes. */
const DISPLAY_FX_GC_TIME_MS = 30 * 60 * 1000;

/**
 * API base URL resolution. Reads `VITE_API_BASE_URL` from the consuming
 * Vite app at runtime; falls back to `/api/v1` to honour the shared
 * monorepo convention. The lookup is guarded so non-Vite hosts (SSR,
 * tests) don't trip on missing globals.
 */
function apiBaseUrl(): string {
  try {
    // `import.meta.env` exists at runtime in any Vite-built bundle. The
    // optional chain + try/catch makes this safe for vitest (node) too.
    const meta = import.meta as unknown as {
      env?: { VITE_API_BASE_URL?: string };
    };
    const fromEnv = meta?.env?.VITE_API_BASE_URL;
    if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  } catch {
    /* swallow — fall through to default */
  }
  return '/api/v1';
}

/** Wire shape — server response for GET /api/v1/fx-rates?from=USD&to={ccy}. */
interface FxRateResponseEnvelope {
  ok?: boolean;
  data?: {
    from?: string;
    to?: string;
    rate?: number;
    fetchedAt?: string | number;
  };
  // Legacy flat shape per INTERFACE-STANDARDS legacy clause.
  rate?: number;
  fetchedAt?: string | number;
  error?: {
    code?: string;
    message?: string;
    user_message?: string;
  };
}

/** Hook result consumed by `<Money>` and any other live-FX surface. */
export interface UseDisplayFxResult {
  /**
   * Live conversion rate from USD → displayCcy.
   * `null` until the first successful fetch (or on error).
   */
  fxRate: number | null;
  /**
   * Epoch ms when the cached rate was fetched.
   * `null` when never loaded.
   */
  fetchedAt: number | null;
  /** Normalised, uppercased display currency code (e.g. 'EUR', 'USD'). */
  ccy: string;
  /** True when the cached rate is older than the staleness threshold. */
  isStale: boolean;
  /** TanStack Query loading state — true during the first fetch. */
  isLoading: boolean;
  /** TanStack Query fetching state — true on background refetch too. */
  isFetching: boolean;
  /** Sanitised error message — null when no error. */
  errorMessage: string | null;
}

/**
 * Live FX rate hook for the shared `<Money>` component.
 *
 * @example
 *   const { fxRate, fetchedAt, isStale } = useDisplayFx('EUR');
 *   if (fxRate != null) console.log(`1 USD = ${fxRate} EUR`);
 */
export function useDisplayFx(
  displayCcy: string | undefined,
): UseDisplayFxResult {
  const target = (displayCcy ?? 'USD').toUpperCase();
  const isUsdTarget = target === 'USD';

  const query: UseQueryResult<{
    rate: number;
    fetchedAt: number;
  }> = useQuery({
    queryKey: ['displayFx', 'USD', target],
    enabled: !isUsdTarget,
    staleTime: DISPLAY_FX_STALE_THRESHOLD_MS,
    gcTime: DISPLAY_FX_GC_TIME_MS,
    refetchOnWindowFocus: true,
    retry: 1,
    queryFn: async () => {
      const url = `${apiBaseUrl()}/fx-rates?from=USD&to=${encodeURIComponent(
        target,
      )}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        let msg = `HTTP ${res.status}`;
        try {
          const parsed = JSON.parse(text) as FxRateResponseEnvelope;
          msg =
            parsed?.error?.user_message ?? parsed?.error?.message ?? msg;
        } catch {
          /* keep HTTP fallback */
        }
        throw new Error(msg);
      }
      const body = (await res.json()) as FxRateResponseEnvelope;
      const rate = body?.data?.rate ?? body?.rate;
      const fetchedAtRaw = body?.data?.fetchedAt ?? body?.fetchedAt;
      if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) {
        throw new Error('FX rate response missing or invalid');
      }
      const fetchedAt =
        typeof fetchedAtRaw === 'number'
          ? fetchedAtRaw
          : fetchedAtRaw
            ? Date.parse(String(fetchedAtRaw))
            : Date.now();
      return { rate, fetchedAt };
    },
  });

  if (isUsdTarget) {
    return {
      fxRate: 1,
      fetchedAt: Date.now(),
      ccy: 'USD',
      isStale: false,
      isLoading: false,
      isFetching: false,
      errorMessage: null,
    };
  }

  const data = query.data;
  const fetchedAt = data?.fetchedAt ?? null;
  const isStale =
    fetchedAt != null && Date.now() - fetchedAt > DISPLAY_FX_STALE_THRESHOLD_MS;

  const errMsg =
    query.error instanceof Error
      ? query.error.message
      : query.error
        ? String(query.error)
        : null;

  return {
    fxRate: data?.rate ?? null,
    fetchedAt,
    ccy: target,
    isStale,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    errorMessage: errMsg,
  };
}

export default useDisplayFx;
